'use client';

import { useCallback, useEffect, useState } from 'react';

import { useRouter } from '@shared/hooks/use-router';
import { STATIC_FORM_ID } from '@/features/form-editing/components/constants/form-edit-constants';

import { AnimatePresence } from '@/shared/components';
import { MIXPANEL_APP_FEATURES, mixpanelEvents } from '@/shared/constants/mixpanel-events';
import { APP_ROUTES, getFilledAppRoute } from '@/shared/constants/navigation';
import { SESSION_STORAGE_KEYS } from '@/shared/constants/storage-keys';
import { useAppCallbacks, useAppStore } from '@/shared/contexts/app-wrapper/providers/app-provider';
import { getSelectedWorkspaceId } from '@/shared/hooks/useSelectWorkspace';
import { getUserDetails } from '@/shared/services/user';
import { emitMixpanelEvent } from '@/shared/utils/mixpanel-utils';
import { setSessionStorageItem } from '@/shared/utils/storage';

import { UserOnboardingModal } from '..';
import { AuthModal } from '../modals/auth-modal';

interface PlaygroundUserOnboardingModalsProps {
	open: boolean;
	onClose: () => void;
	triggerAction: 'publish' | 'settings' | 'publishers' | 'sign_in' | null;
	onLoginComplete?: () => void;
}

function PlaygroundUserOnboardingModals({
	open,
	onClose,
	triggerAction,
	onLoginComplete,
}: PlaygroundUserOnboardingModalsProps) {
	const router = useRouter();
	const setIsAnonymousMode = useAppStore((state) => state.setIsAnonymousMode);
	const refetchUserDetails = useAppCallbacks((state) => state.refetchUserDetails);
	const setUserDetails = useAppStore((state) => state.setUserDetails);
	const setSelectedWorkspaceId = useAppStore((state) => state.setSelectedWorkspaceId);

	const [showAuthModal, setShowAuthModal] = useState(open);
	const [showOnboardingModal, setShowOnboardingModal] = useState(false);
	const [isVisible, setIsVisible] = useState(false);

	useEffect(() => {
		if (open) {
			setShowAuthModal(true);
			setTimeout(() => setIsVisible(true), 100);
		} else {
			setShowAuthModal(false);
			setShowOnboardingModal(false);
			setIsVisible(false);
		}
	}, [open]);

	const handleOnOnboardingComplete = useCallback(() => {
		emitMixpanelEvent(mixpanelEvents.COMPLETED_ONBOARDING, {
			feature: MIXPANEL_APP_FEATURES.FORM_PLAYGROUND,
			trigger_action: triggerAction,
		});
		setSessionStorageItem(SESSION_STORAGE_KEYS.IS_USER_CREATED_VIA_ONBOARDING, 'true');

		setShowAuthModal(false);
		setShowOnboardingModal(false);
		setIsVisible(false);
		onClose();

		onLoginComplete?.();
	}, [triggerAction, onClose, onLoginComplete]);

	const handleAuthSuccess = useCallback(async () => {
		try {
			const userData = await getUserDetails();
			if (!userData) return;

			setShowAuthModal(false);
			setUserDetails(userData);

			const selectedWorkspaceId = getSelectedWorkspaceId({
				allWorkspaces: userData.pages,
				routerWorkspaceId: '',
			});

			if (selectedWorkspaceId) {
				setSelectedWorkspaceId(selectedWorkspaceId);
				setIsAnonymousMode(false);
				handleOnOnboardingComplete();
			} else {
				setShowOnboardingModal(true);
			}
		} catch (error) {
			console.error('Error fetching user details after auth:', error);
			setIsAnonymousMode(false);
		}
	}, [
		setUserDetails,
		setIsAnonymousMode,
		setSelectedWorkspaceId,
		handleOnOnboardingComplete,
	]);

	const handleOnboardingComplete = useCallback(async () => {
		try {
			setShowOnboardingModal(false);
			await refetchUserDetails();
			setIsAnonymousMode(false);
			handleOnOnboardingComplete();
		} catch (error) {
			console.error('Error after onboarding completion:', error);
			setIsAnonymousMode(false);
		}
	}, [setIsAnonymousMode, refetchUserDetails, handleOnOnboardingComplete]);

	const handleClose = useCallback(() => {
		setShowAuthModal(false);
		setShowOnboardingModal(false);
		setIsVisible(false);
		onClose();
	}, [onClose]);

	useEffect(() => {
		router.prefetch(getFilledAppRoute(APP_ROUTES.FORM_EDIT, { id: STATIC_FORM_ID }));
	}, [router]);

	if (!isVisible) return null;

	return (
		<AnimatePresence elementKey="playground-user-onboarding-modals">
			<AuthModal
				open={showAuthModal}
				onAuthSuccess={handleAuthSuccess}
				onClose={handleClose}
				showCloseButton={true}
				closeButtonTooltip="Back to editor"
			/>

			<UserOnboardingModal
				open={showOnboardingModal}
				onComplete={handleOnboardingComplete}
			/>
		</AnimatePresence>
	);
}

export default PlaygroundUserOnboardingModals;
